import React, { useMemo } from 'react';
import { Breadcrumb, Typography } from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  GroupOutlined,
  HomeOutlined,
  LineChartOutlined,
  MailOutlined,
  RightOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import PageContainer from '../common/PageContainer';
import { useTranslation } from '../../hooks/useTranslation';
import { useStatusList, useVariablesGroup } from '../../hooks/parameters';
import Status from '../../modules/settings/Status';
import Parameters from '../../modules/settings/Parameters';
import AssessmentConfig from '../../modules/settings/AssessmentConfig';
import Notifications from '../../modules/settings/Notifications';
import EmailServer from '../../modules/settings/EmailServer';
import TransactionRules from '../../modules/settings/TransactionRules';
import settingsMenuData from '../../modules/settings/config/parameters.json';
import './Settings.less';

const { Text } = Typography;

type SettingsKey =
  | 'overview'
  | 'statusIssues'
  | 'groupParameters'
  | 'impactAssessment'
  | 'notifications'
  | 'emailServer'
  | 'transactionRules';

const OVERVIEW_PATH = '/settings';
const DEFAULT_KEY: SettingsKey = 'overview';

const subPageMap: Partial<Record<SettingsKey, React.ReactNode>> = {
  statusIssues: <Status />,
  groupParameters: <Parameters />,
  impactAssessment: <AssessmentConfig />,
  notifications: <Notifications />,
  emailServer: <EmailServer />,
  transactionRules: <TransactionRules />,
};

interface OverviewCard {
  key: string;
  path: string;
  icon: React.ReactNode;
  titleKey: string;
  descriptionKey: string;
  snippet: string;
}

const SettingsOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: statuses, isLoading: isLoadingStatuses } = useStatusList();
  const { data: variableGroups, isLoading: isLoadingVariableGroups } = useVariablesGroup();

  const formatCount = (template: string, count: number) =>
    template.replace('{count}', String(count));

  const loadingLabel = t('settings.content.overview.snippets.loading');

  const cards: OverviewCard[] = [
    {
      key: 'statusIssues',
      path: '/settings/status-issues',
      icon: <CheckCircleOutlined />,
      titleKey: 'settings.menu.statusIssues',
      descriptionKey: 'settings.content.statusIssues.description',
      snippet: isLoadingStatuses
        ? loadingLabel
        : formatCount(
            t('settings.content.overview.snippets.statusIssues'),
            Array.isArray(statuses) ? statuses.length : 0,
          ),
    },
    {
      key: 'groupParameters',
      path: '/settings/group-parameters',
      icon: <GroupOutlined />,
      titleKey: 'settings.menu.groupParameters',
      descriptionKey: 'settings.content.groupParameters.description',
      snippet: t('settings.content.overview.snippets.groupParameters'),
    },
    {
      key: 'impactAssessment',
      path: '/settings/impact-assessment',
      icon: <LineChartOutlined />,
      titleKey: 'settings.menu.impactAssessment',
      descriptionKey: 'settings.content.impactAssessment.description',
      snippet: isLoadingVariableGroups
        ? loadingLabel
        : formatCount(
            t('settings.content.overview.snippets.impactAssessment'),
            Array.isArray(variableGroups) ? variableGroups.length : 0,
          ),
    },
    {
      key: 'notifications',
      path: '/settings/notifications',
      icon: <BellOutlined />,
      titleKey: 'settings.menu.notifications',
      descriptionKey: 'settings.content.notifications.description',
      snippet: t('settings.content.overview.snippets.notifications'),
    },
    {
      key: 'emailServer',
      path: '/settings/email-server',
      icon: <MailOutlined />,
      titleKey: 'settings.menu.emailServer',
      descriptionKey: 'settings.content.emailServer.cardDescription',
      snippet: t('settings.content.overview.snippets.emailServer'),
    },
    {
      key: 'transactionRules',
      path: '/settings/transaction-rules',
      icon: <SwapOutlined />,
      titleKey: 'settings.menu.transactionRules',
      descriptionKey: 'settings.content.transactionRules.description',
      snippet: t('settings.content.overview.snippets.transactionRules'),
    },
  ];

  return (
    <div className="gt-settings-home-grid">
      {cards.map((card) => (
        <button
          key={card.key}
          type="button"
          onClick={() => navigate(card.path)}
          className="gt-settings-card"
          aria-label={t(card.titleKey)}
        >
          <div className="gt-settings-card-header">
            <span className="gt-settings-card-icon">{card.icon}</span>
            <Text className="gt-settings-card-title">{t(card.titleKey)}</Text>
          </div>
          <Text className="gt-settings-card-description">{t(card.descriptionKey)}</Text>
          <div className="gt-settings-card-footer">
            <Text className="gt-settings-card-status">{card.snippet}</Text>
            <span className="gt-settings-card-cta">
              {t('settings.content.overview.cardCta')}
              <RightOutlined />
            </span>
          </div>
        </button>
      ))}
    </div>
  );
};

function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedKey = useMemo<SettingsKey>(() => {
    const item = settingsMenuData.find((i) => location.pathname === i.path);
    return (item?.key as SettingsKey) ?? DEFAULT_KEY;
  }, [location.pathname]);

  const isOverview = selectedKey === 'overview';

  if (!isOverview) {
    return <>{subPageMap[selectedKey] ?? null}</>;
  }

  return (
    <PageContainer
      bordered={false}
      title={t('settings.content.overview.title')}
      subTitle={t('settings.content.overview.subtitle')}
      headerPaddingBottom={true}
    >
      <SettingsOverview />
    </PageContainer>
  );
}

export default Settings;
